'use client';

import { Popover } from '@base-ui/react/popover';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { type CSSProperties, startTransition, useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { ExpandIcon } from '../ExpandIcon';
import { useRipplePress } from '../useRipplePress';
import { OptionsList } from './OptionsList';

type GenericFilterStoreHook<TFilterOptions extends string> = <T>(
  selector: (state: {
    openedFilters: {
      normalBar: Record<TFilterOptions, boolean>;
      stickyBar: Record<TFilterOptions, boolean>;
    };
    handleOpenedFilters: (
      filter: TFilterOptions,
      bar: 'normalBar' | 'stickyBar',
      state: boolean,
    ) => void;
  }) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => T;

type Props<TFilterOptions extends string> = {
  label: TFilterOptions;
  options: { label: string; count: number }[];
  selectedOptions: string[];
  onChange: (selected: string[]) => void;
  isStickyBar: boolean;
  useStore: GenericFilterStoreHook<TFilterOptions>;
};

const triggerClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: '2',
  rowGap: '2',
  borderTopLeftRadius: '[64px]',
  borderTopRightRadius: '[64px]',
  borderBottomRightRadius: '[64px]',
  borderBottomLeftRadius: '[64px]',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  minHeight: '10',
  cursor: 'pointer',
  borderTopWidth: 'thin',
  borderRightWidth: 'thin',
  borderBottomWidth: 'thin',
  borderLeftWidth: 'thin',
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopColor: 'neutral.lighter',
  borderRightColor: 'neutral.lighter',
  borderBottomColor: 'neutral.lighter',
  borderLeftColor: 'neutral.lighter',
  backgroundColor: 'white',
  textAlign: 'left',
  fontFamily: '[inherit]',
  userSelect: 'none',
  position: 'relative',
  overflowX: 'hidden',
  overflowY: 'hidden',
  _focus: {
    outlineStyle: 'none',
  },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: 'blue.light',
    outlineOffset: '[-2px]',
  },
  _after: {
    content: '""',
    display: 'block',
    position: 'absolute',
    width: 'full',
    height: 'full',
    top: '0',
    left: '0',
    pointerEvents: 'none',
    backgroundImage: '[radial-gradient(circle,token(colors.green.main) 10%,transparent 10.01%)]',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: '[50%]',
    backgroundPositionY: '[50%]',
    // No resting `transition` here: the async Panda stylesheet applies these
    // properties over the already-painted (inline critical CSS) trigger, and a
    // resting transition would animate that change into a green ripple on load.
    // The ripple is driven by the `ripple` keyframe, which useRipplePress runs
    // by toggling `data-rippling` on pointer/keyboard activation.
    transform: '[scale(0)]',
    opacity: '0',
  },
  '&[data-rippling]::after': {
    animationName: 'ripple',
    animationDuration: '[0.4s]',
    animationTimingFunction: 'ease-out',
  },
});

const triggerActiveClass = css({
  backgroundColor: 'green.light',
  borderTopWidth: 'medium',
  borderRightWidth: 'medium',
  borderBottomWidth: 'medium',
  borderLeftWidth: 'medium',
  borderTopColor: 'green.main',
  borderRightColor: 'green.main',
  borderBottomColor: 'green.main',
  borderLeftColor: 'green.main',
});

const triggerLabelClass = css({
  fontSize: 'sm',
  fontWeight: 'medium',
  color: 'var(--label-color)',
});

const triggerLabelActiveClass = css({ color: 'var(--label-color)' });

const popupClass = css({
  backgroundColor: 'white',
  borderTopLeftRadius: '3',
  borderTopRightRadius: '3',
  borderBottomRightRadius: '3',
  borderBottomLeftRadius: '3',
  boxShadow: '[0 4px 12px rgba(0,0,0,0.15)]',
  minWidth: '[225px]',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  outlineStyle: 'none',
});

const popupHeaderClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBottom: '2',
  fontSize: 'sm',
  fontWeight: 'medium',
  color: 'neutral.dark',
});

export const MultiSelectDropdown = <TFilterOptions extends string>({
  label,
  options,
  selectedOptions,
  onChange,
  isStickyBar,
  useStore,
}: Props<TFilterOptions>) => {
  const handleOpenKey = isStickyBar ? 'stickyBar' : 'normalBar';
  const isOpen = useStore(state => state.openedFilters[handleOpenKey][label], shallow);
  const handleOpenedFilters = useStore(state => state.handleOpenedFilters, shallow);

  const { ref: rippleRef, elementRef: buttonRef } = useRipplePress();
  const hasSelection = selectedOptions.length > 0;

  const handleToggleOpen = useCallback(() => {
    startTransition(() => {
      handleOpenedFilters(label, handleOpenKey, !isOpen);
    });
  }, [handleOpenedFilters, handleOpenKey, isOpen, label]);

  const handleClose = useCallback(() => {
    startTransition(() => {
      handleOpenedFilters(label, handleOpenKey, false);
    });
    buttonRef.current?.focus();
  }, [handleOpenedFilters, handleOpenKey, label]);

  const updateSelection = useCallback(
    (option: string) => {
      const isSelected = selectedOptions.includes(option);
      onChange(
        isSelected ? selectedOptions.filter(o => o !== option) : [...selectedOptions, option],
      );
    },
    [selectedOptions, onChange],
  );

  const selectedOptionsSet = useMemo(() => new Set(selectedOptions), [selectedOptions]);
  const displayLabel = hasSelection ? `${label} +${selectedOptions.length}` : label;

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={open => handleOpenedFilters(label, handleOpenKey, open)}
    >
      <Popover.Trigger
        ref={rippleRef}
        onClick={handleToggleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${label}-dropdown-options`}
        className={cx(triggerClass, hasSelection && triggerActiveClass)}
      >
        <span
          className={cx(triggerLabelClass, hasSelection && triggerLabelActiveClass)}
          style={
            {
              '--label-color': hasSelection ? '#008254' : '#64666A',
            } as CSSProperties
          }
        >
          {displayLabel}
        </span>
        <ExpandIcon width={15} color={hasSelection ? '#008254' : '#64666A'} />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner sideOffset={8} align="start" style={{ zIndex: 200 }}>
          <Popover.Popup
            id={`${label}-dropdown-options`}
            role="listbox"
            aria-label={label}
            aria-multiselectable="true"
            className={popupClass}
          >
            <div className={popupHeaderClass}>{label}</div>
            <OptionsList
              options={options}
              selectedOptionsSet={selectedOptionsSet}
              onOptionSelect={updateSelection}
              onClose={handleClose}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};
